import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Coins as CoinsIcon, Wallet, Sparkles, ArrowRight, ShoppingBag, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Coins() {
  const { user, ready, refresh } = useAuth();
  const nav = useNavigate();
  const [packages, setPackages] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [amount, setAmount] = useState("");

  const reload = () => {
    api.get("/coin-shop").then(r=>setPackages(r.data));
    if (user) {
      api.get("/me/earnings").then(r=>setEarnings(r.data));
      api.get("/payouts/mine").then(r=>setPayouts(r.data));
      api.get("/me/purchases").then(r=>setPurchases(r.data));
    }
  };
  useEffect(() => { reload(); }, [user]);

  if (ready && !user) { nav("/login"); return null; }
  if (!earnings) return <div className="p-8 text-white/60">Loading…</div>;

  const buy = async (pid) => {
    try { await api.post("/coin-shop/purchase", { package_id: pid }); toast.success("Coins added!"); await refresh(); reload(); }
    catch(e){ toast.error(apiErr(e)); }
  };
  const cashout = async () => {
    if (!earnings.cashout_eligible) { toast.error("Not eligible yet — keep earning to reach the $5 threshold."); return; }
    try { await api.post("/payouts/request", { coins: Number(amount), method: "manual" }); toast.success("Cashout requested"); setAmount(""); await refresh(); reload(); }
    catch(e){toast.error(apiErr(e));}
  };

  const hidden = earnings.hidden;
  const pct = Math.round(earnings.progress_pct);
  const target = earnings.cashout_target_usd;

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 pt-6">
      {/* Progress card (replaces raw balance when hidden) */}
      <div className="rounded-2xl border border-white/10 p-5 sm:p-6 bg-gradient-to-br from-[#FFD700]/10 to-[#FF2E63]/5">
        {hidden ? (
          <>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl coin-pill flex items-center justify-center"><Lock size={20}/></div>
              <div className="flex-1">
                <div className="text-xs text-white/60">Earnings hidden until cashout eligible</div>
                <div className="h-font text-2xl font-extrabold text-[#FFD700]" data-testid="earnings-progress-pct">{pct}% to your first ${target.toFixed(2)} cashout</div>
              </div>
            </div>
            <div className="mt-4 h-3 bg-white/10 rounded-full overflow-hidden" data-testid="progress-bar">
              <div className="h-full bg-gradient-to-r from-[#FFD700] to-[#FF2E63]" style={{width: pct + "%"}}/>
            </div>
            <p className="text-xs text-white/50 mt-3">Keep uploading, sharing, and getting views — your progress bar fills as you earn. Once you reach ${target.toFixed(2)} the amount and cashout button unlock.</p>
          </>
        ) : (
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl coin-pill flex items-center justify-center"><CoinsIcon size={22}/></div>
            <div>
              <div className="text-xs text-white/60">Your balance</div>
              <div className="h-font text-3xl font-extrabold text-[#FFD700]" data-testid="coin-balance">{Math.floor(earnings.current_coins)} coins</div>
              <div className="text-xs text-white/60 mt-1">≈ ${earnings.usd_value.toFixed(2)} USD · ready to cash out</div>
            </div>
          </div>
        )}
      </div>

      <section className="mt-8">
        <h2 className="h-font text-2xl font-bold flex items-center gap-2"><Sparkles size={20} className="text-[#08D9D6]"/> Coin Shop</h2>
        <p className="text-xs text-white/50 mt-1">Buy coins to unlock premium videos, tip creators, subscribe, boost or feature your uploads.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          {packages.map(p => (
            <div key={p.id} data-testid={`pack-${p.id}`} className={`rounded-2xl p-4 border ${p.highlight?'border-[#FF2E63] bg-[#FF2E63]/10':'border-white/10 bg-white/5'}`}>
              <div className="text-xs uppercase tracking-widest text-white/60">{p.label}</div>
              <div className="h-font text-2xl font-extrabold mt-1 text-[#FFD700]">{p.coins}</div>
              <div className="text-xs text-white/60">coins</div>
              <button onClick={()=>buy(p.id)} data-testid={`buy-${p.id}`}
                      className="mt-4 w-full h-10 rounded-full bg-[#08D9D6] text-black font-bold text-sm jx-btn hover:bg-[#06B2B0] inline-flex items-center justify-center gap-1">
                <ShoppingBag size={14}/> ${p.usd.toFixed(2)}
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-white/10 p-5">
          <h2 className="h-font text-xl font-bold flex items-center gap-2"><Wallet size={18}/> Cash out</h2>
          <p className="text-xs text-white/60 mt-1">Min ${target.toFixed(2)} · Rate ${earnings.rules.coin_to_usd}/coin</p>
          {earnings.cashout_eligible ? (
            <div className="flex gap-2 mt-4">
              <input value={amount} onChange={e=>setAmount(e.target.value)} type="number" placeholder="Coins" data-testid="cashout-amount"
                     className="flex-1 h-11 rounded-xl bg-white/5 border border-white/10 px-4 focus:outline-none focus:border-[#08D9D6]"/>
              <button onClick={cashout} data-testid="cashout-btn" className="jx-btn px-4 h-11 rounded-xl bg-[#FF2E63] font-bold text-sm inline-flex items-center gap-2 hover:bg-[#e02757]">
                Request <ArrowRight size={14}/>
              </button>
            </div>
          ) : (
            <div className="mt-4 rounded-xl bg-white/5 border border-white/10 p-4 text-sm text-white/60 inline-flex items-center gap-2">
              <Lock size={14} className="text-[#FFD700]"/> Cashout unlocks when your progress bar reaches 100%.
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-white/10 p-5">
          <h2 className="h-font text-xl font-bold">Payout history</h2>
          <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
            {payouts.length===0 && <div className="text-sm text-white/50">No cashouts yet.</div>}
            {payouts.map(p => (
              <div key={p.id} className="flex justify-between text-sm border-b border-white/5 pb-2">
                <div>
                  <div className="font-semibold">{p.coins} coins</div>
                  <div className="text-xs text-white/50">{new Date(p.created_at).toLocaleString()}</div>
                </div>
                <span className={`chip px-2 py-0.5 rounded-full text-[11px] ${p.status==='paid'?'text-[#08D9D6]':p.status==='rejected'?'text-[#FF2E63]':''}`}>{p.status}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {purchases.length>0 && (
        <section className="mt-8">
          <h2 className="h-font text-xl font-bold">Your unlocked videos</h2>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {purchases.map(p => (
              <a key={p.video.id} href={`/watch/${p.video.id}`} className="rounded-xl overflow-hidden border border-white/10 bg-white/5">
                <img src={p.video.thumbnail_url || undefined} alt="" className="w-full aspect-video object-cover"/>
                <div className="p-3">
                  <div className="text-sm font-semibold line-clamp-2">{p.video.title}</div>
                  <div className="text-xs text-[#FFD700] mt-1">{p.coins_paid} coins · {new Date(p.created_at).toLocaleDateString()}</div>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      {!hidden && (
        <section className="mt-8">
          <h2 className="h-font text-xl font-bold">Earnings history</h2>
          <div className="mt-3 rounded-2xl border border-white/10 divide-y divide-white/5">
            {earnings.history.length===0 && <div className="p-5 text-sm text-white/50">No earnings yet - upload a video to start.</div>}
            {earnings.history.slice(0,20).map(e => (
              <div key={e.id} className="flex justify-between px-4 py-3 text-sm">
                <div>
                  <div className="font-medium">{e.reason}</div>
                  <div className="text-xs text-white/50">{new Date(e.created_at).toLocaleString()}</div>
                </div>
                <div className="text-[#FFD700] font-bold">+{e.amount?.toFixed?.(1) ?? e.amount}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
